import React, { useState, useEffect, useCallback } from 'react';
import { Code, TrendingUp, ShoppingBag, Bot, Building, Calendar } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { translations } from '../data/mockData';
import { fetchServices } from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

// AI Service deposit amount
const AI_DEPOSIT_AMOUNT = 200;

// Button text translations
const buttonText = {
  en: {
    getStarted: 'Get Started',
    bookSession: 'Book Strategy Session',
    depositFrom: 'Deposit',
    totalInvestment: 'Total Investment from'
  },
  es: {
    getStarted: 'Comenzar',
    bookSession: 'Reservar Sesión Estratégica',
    depositFrom: 'Depósito',
    totalInvestment: 'Inversión Total desde'
  },
  de: {
    getStarted: 'Jetzt Starten',
    bookSession: 'Strategiesitzung Buchen',
    depositFrom: 'Anzahlung',
    totalInvestment: 'Gesamtinvestition ab'
  }
};

// ServiceCard component moved outside to avoid re-renders
const ServiceCard = ({ service, language, onServiceClick }) => {
  const isAiService = service.category === 'ai';
  const bt = buttonText[language] || buttonText.es;
  
  return (
    <div className="group p-6 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] hover:border-[#D4AF37]/50 transition-all duration-300 hover:scale-[1.02] flex flex-col h-full relative overflow-hidden">
      {/* Hover glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/0 group-hover:from-[#D4AF37]/5 group-hover:to-transparent transition-all duration-300"></div>
      
      {/* AI Badge for AI services */}
      {isAiService && (
        <div className="absolute top-4 right-4 px-2 py-1 bg-[#8B5CF6]/20 border border-[#8B5CF6]/40 rounded-full text-xs text-[#8B5CF6] font-semibold">
          AI
        </div>
      )}
      
      <div className="relative z-10 flex-1">
        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors duration-300">
          {service.name[language]}
        </h3>
        <p className="text-base text-gray-400 mb-4 leading-relaxed">
          {service.description[language]}
        </p>
        
        {/* Pricing - Different display for AI services */}
        {isAiService ? (
          <div className="mb-6">
            <div className="flex items-baseline space-x-2 mb-1">
              <span className="text-lg text-gray-400">{bt.depositFrom}:</span>
              <span className="text-3xl font-bold text-gold-metallic">
                €{AI_DEPOSIT_AMOUNT}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              {bt.totalInvestment} <span className="text-[#8B5CF6]">€{service.price.toLocaleString()}</span>
              {service.recurring && <span className="text-gray-400">/month</span>}
            </div>
          </div>
        ) : (
          <div className="flex items-baseline space-x-2 mb-6">
            <span className="text-3xl font-bold text-gold-metallic">
              €{service.price.toLocaleString()}
            </span>
            {service.recurring && (
              <span className="text-sm text-gray-400">/month</span>
            )}
          </div>
        )}
      </div>
      
      {/* Button - Different for AI services */}
      <button
        data-testid={`service-${service.id}-add-btn`}
        onClick={() => onServiceClick(service)}
        className={`relative z-10 w-full px-6 py-3 rounded-full font-bold text-xs uppercase tracking-widest transition-all duration-300 flex items-center justify-center space-x-2 ${
          isAiService 
            ? 'bg-gradient-to-r from-[#8B5CF6] to-[#A855F7] text-white hover:shadow-[0_0_20px_rgba(139,92,246,0.4)]'
            : 'bg-gradient-to-r from-[#D4AF37] to-[#F4C542] text-[#141619] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]'
        }`}
      >
        {isAiService ? (
          <>
            <Calendar className="w-4 h-4" />
            <span>{bt.bookSession}</span>
          </>
        ) : (
          <>
            <ShoppingBag className="w-4 h-4" />
            <span>{bt.getStarted}</span>
          </>
        )}
      </button>
    </div>
  );
};

const Services = () => {
  const { language } = useLanguage();
  const { addToCart } = useCart();
  const t = translations[language].services;
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [showBusinessForm, setShowBusinessForm] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    industry: '',
    primaryGoal: '',
    currentTools: '',
    targetAudience: '',
    timeline: '',
    monthlyBudget: ''
  });

  // Form labels translations
  const formLabels = {
    en: {
      title: 'Tell Us About Your Project',
      subtitle: 'Help us understand your needs to provide tailored solutions',
      businessName: 'Business Name',
      industry: 'Industry',
      primaryGoal: 'Primary Project Goal',
      primaryGoalPlaceholder: 'Select your primary goal...',
      goalOptions: {
        leadGen: 'Lead Generation',
        processAutomation: 'Process Automation',
        webApp: 'Web/App Development',
        marketing: 'Digital Marketing',
        aiIntegration: 'AI Integration'
      },
      currentTools: 'Current Tools Used',
      currentToolsPlaceholder: 'E.g., CRM, Email marketing, Spreadsheets, n8n, Make...',
      targetAudience: 'Target Audience',
      targetAudiencePlaceholder: 'Who are your ideal customers?',
      timeline: 'Timeline',
      timelineOptions: {
        asap: 'ASAP',
        '1-2weeks': '1-2 weeks',
        '1month': '1 month',
        '2-3months': '2-3 months',
        flexible: 'Flexible'
      },
      monthlyBudget: 'Monthly Budget for Automation/Ads',
      budgetOptions: {
        under500: 'Under €500',
        '500-1000': '€500 - €1,000',
        '1000-2500': '€1,000 - €2,500',
        '2500-5000': '€2,500 - €5,000',
        over5000: '€5,000+'
      },
      cancel: 'Cancel',
      addToCart: 'Add to Cart',
      required: 'Please fill in all required fields'
    },
    es: {
      title: 'Cuéntanos Sobre Tu Proyecto',
      subtitle: 'Ayúdanos a entender tus necesidades para ofrecerte soluciones personalizadas',
      businessName: 'Nombre del Negocio',
      industry: 'Industria',
      primaryGoal: 'Objetivo Principal del Proyecto',
      primaryGoalPlaceholder: 'Selecciona tu objetivo principal...',
      goalOptions: {
        leadGen: 'Generación de Leads',
        processAutomation: 'Automatización de Procesos',
        webApp: 'Desarrollo Web/App',
        marketing: 'Marketing Digital',
        aiIntegration: 'Integración de IA'
      },
      currentTools: 'Herramientas Actuales',
      currentToolsPlaceholder: 'Ej., CRM, Email marketing, Hojas de cálculo, n8n, Make...',
      targetAudience: 'Público Objetivo',
      targetAudiencePlaceholder: '¿Quiénes son tus clientes ideales?',
      timeline: 'Plazo',
      timelineOptions: {
        asap: 'Lo antes posible',
        '1-2weeks': '1-2 semanas',
        '1month': '1 mes',
        '2-3months': '2-3 meses',
        flexible: 'Flexible'
      },
      monthlyBudget: 'Presupuesto Mensual para Automatización/Ads',
      budgetOptions: {
        under500: 'Menos de €500',
        '500-1000': '€500 - €1,000',
        '1000-2500': '€1,000 - €2,500',
        '2500-5000': '€2,500 - €5,000',
        over5000: '€5,000+'
      },
      cancel: 'Cancelar',
      addToCart: 'Añadir al Carrito',
      required: 'Por favor complete todos los campos requeridos'
    },
    de: {
      title: 'Erzählen Sie uns von Ihrem Projekt',
      subtitle: 'Helfen Sie uns, Ihre Bedürfnisse zu verstehen, um maßgeschneiderte Lösungen anzubieten',
      businessName: 'Firmenname',
      industry: 'Branche',
      primaryGoal: 'Hauptziel des Projekts',
      primaryGoalPlaceholder: 'Wählen Sie Ihr Hauptziel...',
      goalOptions: {
        leadGen: 'Lead-Generierung',
        processAutomation: 'Prozessautomatisierung',
        webApp: 'Web-/App-Entwicklung',
        marketing: 'Digitales Marketing',
        aiIntegration: 'KI-Integration'
      },
      currentTools: 'Aktuelle Tools',
      currentToolsPlaceholder: 'Z.B., CRM, E-Mail-Marketing, Tabellen, n8n, Make...',
      targetAudience: 'Zielgruppe',
      targetAudiencePlaceholder: 'Wer sind Ihre idealen Kunden?',
      timeline: 'Zeitrahmen',
      timelineOptions: {
        asap: 'So schnell wie möglich',
        '1-2weeks': '1-2 Wochen',
        '1month': '1 Monat',
        '2-3months': '2-3 Monate',
        flexible: 'Flexibel'
      },
      monthlyBudget: 'Monatliches Budget für Automatisierung/Ads',
      budgetOptions: {
        under500: 'Unter €500',
        '500-1000': '€500 - €1.000',
        '1000-2500': '€1.000 - €2.500',
        '2500-5000': '€2.500 - €5.000',
        over5000: '€5.000+'
      },
      cancel: 'Abbrechen',
      addToCart: 'In den Warenkorb',
      required: 'Bitte füllen Sie alle erforderlichen Felder aus'
    }
  };

  const fl = formLabels[language];

  // AI Services section translations
  const aiSection = {
    en: {
      title: 'AI Automation & Workflow',
      description: 'Supercharge your business with intelligent automation',
      items: [
        'Custom AI Chatbots',
        'n8n/Make Workflow Automation',
        'AI-Powered Lead Generation',
        'Smart CRM Integration',
        'Predictive Analytics',
        'Process Optimization'
      ]
    },
    es: {
      title: 'Automatización IA & Workflows',
      description: 'Potencia tu negocio con automatización inteligente',
      items: [
        'Chatbots IA Personalizados',
        'Automatización con n8n/Make',
        'Generación de Leads con IA',
        'Integración CRM Inteligente',
        'Analítica Predictiva',
        'Optimización de Procesos'
      ]
    },
    de: {
      title: 'KI-Automatisierung & Workflows',
      description: 'Steigern Sie Ihr Geschäft mit intelligenter Automatisierung',
      items: [
        'Individuelle KI-Chatbots',
        'n8n/Make Workflow-Automatisierung',
        'KI-gestützte Lead-Generierung',
        'Intelligente CRM-Integration',
        'Prädiktive Analytik',
        'Prozessoptimierung'
      ]
    }
  };

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const data = await fetchServices();
      setServices(data);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const webServices = services.filter(s => s.category === 'web');
  const marketingServices = services.filter(s => s.category === 'marketing');
  const aiServices = services.filter(s => s.category === 'ai');

  const handleServiceClick = useCallback((service) => {
    setSelectedService(service);
    setShowBusinessForm(true);
    setBusinessInfo({
      businessName: '',
      industry: '',
      primaryGoal: '',
      currentTools: '',
      targetAudience: '',
      timeline: '',
      monthlyBudget: ''
    });
  }, []);

  const handleBusinessFormSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!businessInfo.businessName || !businessInfo.primaryGoal) {
      toast.error(fl.required);
      return;
    }

    // Store business info with the service
    const serviceWithBusinessInfo = {
      ...selectedService,
      businessInfo: businessInfo
    };

    addToCart(serviceWithBusinessInfo);
    setShowBusinessForm(false);
    toast.success(
      language === 'en' ? 'Service added to cart with your business details' : 
      language === 'es' ? 'Servicio añadido al carrito con los detalles de tu negocio' : 
      'Service mit Ihren Geschäftsdetails zum Warenkorb hinzugefügt'
    );
  };

  if (loading) {
    return (
      <section id="services" className="py-24 bg-gradient-to-b from-[#141619] to-[#0a0c0f] circuit-pattern">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 text-center">
          <div className="text-[#D4AF37] text-xl">Loading services...</div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="services" className="py-24 bg-gradient-to-b from-[#141619] to-[#0a0c0f] circuit-pattern relative">
        {/* Decorative line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent"></div>
        
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              <span className="text-gold-metallic">{t.title.split(' ')[0]}</span> {t.title.split(' ').slice(1).join(' ')}
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t.subtitle}
            </p>
          </div>

          {/* AI Automation Section - NEW */}
          {aiServices.length > 0 && (
            <div className="mb-20">
              <div className="flex items-center space-x-3 mb-8">
                <div className="p-3 bg-gradient-to-br from-[#8B5CF6] to-[#A855F7] rounded-xl">
                  <Bot className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    {aiSection[language].title}
                  </h3>
                  <p className="text-base text-gray-400">{aiSection[language].description}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {aiServices.map((service) => (
                  <ServiceCard key={service.id} service={service} language={language} onServiceClick={handleServiceClick} />
                ))}
              </div>

              {/* Feature List */}
              <div className="mt-8 p-6 bg-[#1a1d23]/50 backdrop-blur-sm rounded-2xl border border-[#2a3441]">
                <h4 className="text-lg font-semibold text-white mb-4">
                  {language === 'en' ? "What's Included:" : language === 'es' ? 'Qué Incluye:' : 'Was ist enthalten:'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {aiSection[language].items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-[#8B5CF6] rounded-full"></div>
                      <span className="text-sm text-gray-400">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Web Design & Development */}
          <div className="mb-20">
            <div className="flex items-center space-x-3 mb-8">
              <div className="p-3 bg-gradient-to-br from-[#D4AF37] to-[#F4C542] rounded-xl">
                <Code className="w-8 h-8 text-[#141619]" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-white">
                  {t.webDesign.title}
                </h3>
                <p className="text-base text-gray-400">{t.webDesign.description}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {webServices.map((service) => (
                <ServiceCard key={service.id} service={service} language={language} onServiceClick={handleServiceClick} />
              ))}
            </div>

            {/* Feature List */}
            <div className="mt-8 p-6 bg-[#1a1d23]/50 backdrop-blur-sm rounded-2xl border border-[#2a3441]">
              <h4 className="text-lg font-semibold text-white mb-4">
                {language === 'en' ? "What's Included:" : language === 'es' ? 'Qué Incluye:' : 'Was ist enthalten:'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {t.webDesign.items.map((item, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#D4AF37] rounded-full"></div>
                    <span className="text-sm text-gray-400">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Digital Marketing */}
          <div>
            <div className="flex items-center space-x-3 mb-8">
              <div className="p-3 bg-gradient-to-br from-[#5A6F8F] to-[#6A7F9F] rounded-xl">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-white">
                  {t.digitalMarketing.title}
                </h3>
                <p className="text-base text-gray-400">{t.digitalMarketing.description}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {marketingServices.map((service) => (
                <ServiceCard key={service.id} service={service} language={language} onServiceClick={handleServiceClick} />
              ))}
            </div>

            {/* Feature List */}
            <div className="mt-8 p-6 bg-[#1a1d23]/50 backdrop-blur-sm rounded-2xl border border-[#2a3441]">
              <h4 className="text-lg font-semibold text-white mb-4">
                {language === 'en' ? "What's Included:" : language === 'es' ? 'Qué Incluye:' : 'Was ist enthalten:'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {t.digitalMarketing.items.map((item, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#5A6F8F] rounded-full"></div>
                    <span className="text-sm text-gray-400">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Information Form Modal */}
      <Dialog open={showBusinessForm} onOpenChange={setShowBusinessForm}>
        <DialogContent className="bg-[#1a1d23] border-[#2a3441] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gold-metallic flex items-center space-x-2">
              <Building className="w-6 h-6" />
              <span>{fl.title}</span>
            </DialogTitle>
            <p className="text-gray-400 text-sm mt-2">
              {fl.subtitle}
            </p>
          </DialogHeader>
          
          {selectedService && (
            <div className={`mt-4 p-4 rounded-xl border ${
              selectedService.category === 'ai' 
                ? 'bg-[#8B5CF6]/10 border-[#8B5CF6]/30' 
                : 'bg-[#141619] border-[#D4AF37]/30'
            }`}>
              <p className="text-sm text-gray-400">
                {language === 'en' ? 'Selected Service:' : language === 'es' ? 'Servicio Seleccionado:' : 'Ausgewählter Service:'}
              </p>
              <p className="text-lg font-bold text-white">{selectedService.name[language]}</p>
              
              {selectedService.category === 'ai' ? (
                <div className="mt-2">
                  <div className="flex items-baseline space-x-2">
                    <span className="text-sm text-gray-400">
                      {language === 'en' ? 'Strategy Session Deposit:' : language === 'es' ? 'Depósito Sesión Estratégica:' : 'Strategiesitzung Anzahlung:'}
                    </span>
                    <span className="text-2xl font-bold text-[#8B5CF6]">€{AI_DEPOSIT_AMOUNT}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {language === 'en' ? 'Total Investment from' : language === 'es' ? 'Inversión Total desde' : 'Gesamtinvestition ab'}{' '}
                    <span className="text-gold-metallic">€{selectedService.price.toLocaleString()}</span>
                    {selectedService.recurring && <span className="text-gray-400">/mo</span>}
                  </p>
                  
                  {/* Deposit Note */}
                  <div className="mt-3 p-3 bg-[#8B5CF6]/5 rounded-lg border border-[#8B5CF6]/20">
                    <p className="text-xs text-gray-400 leading-relaxed">
                      {language === 'en' 
                        ? '💡 This €200 deposit is a commitment fee and will be deducted from your final project total.'
                        : language === 'es'
                        ? '💡 Este depósito de €200 es una cuota de compromiso y se descontará del total final de tu proyecto.'
                        : '💡 Diese Anzahlung von €200 ist eine Verpflichtungsgebühr und wird von Ihrem endgültigen Projektbetrag abgezogen.'
                      }
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-2xl font-bold text-gold-metallic">
                  €{selectedService.price.toLocaleString()}{selectedService.recurring ? '/mo' : ''}
                </p>
              )}
            </div>
          )}
          
          <form onSubmit={handleBusinessFormSubmit} className="space-y-5 mt-4">
            {/* Business Name & Industry Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {fl.businessName} <span className="text-red-400">*</span>
                </label>
                <Input
                  data-testid="business-name-input"
                  required
                  value={businessInfo.businessName}
                  onChange={(e) => setBusinessInfo({...businessInfo, businessName: e.target.value})}
                  className="bg-[#141619] border-[#2a3441] text-white focus:border-[#D4AF37]"
                  placeholder={language === 'en' ? 'Your Company Name' : language === 'es' ? 'Nombre de tu Empresa' : 'Ihr Firmenname'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {fl.industry}
                </label>
                <Input
                  data-testid="industry-input"
                  value={businessInfo.industry}
                  onChange={(e) => setBusinessInfo({...businessInfo, industry: e.target.value})}
                  className="bg-[#141619] border-[#2a3441] text-white focus:border-[#D4AF37]"
                  placeholder={language === 'en' ? 'E.g., E-commerce, Healthcare' : language === 'es' ? 'Ej., E-commerce, Salud' : 'Z.B., E-Commerce, Gesundheit'}
                />
              </div>
            </div>

            {/* Primary Goal */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {fl.primaryGoal} <span className="text-red-400">*</span>
              </label>
              <select
                data-testid="primary-goal-select"
                required
                value={businessInfo.primaryGoal}
                onChange={(e) => setBusinessInfo({...businessInfo, primaryGoal: e.target.value})}
                className="w-full px-4 py-2 bg-[#141619] border border-[#2a3441] text-white rounded-md focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="">{fl.primaryGoalPlaceholder}</option>
                <option value="leadGen">{fl.goalOptions.leadGen}</option>
                <option value="processAutomation">{fl.goalOptions.processAutomation}</option>
                <option value="webApp">{fl.goalOptions.webApp}</option>
                <option value="marketing">{fl.goalOptions.marketing}</option>
                <option value="aiIntegration">{fl.goalOptions.aiIntegration}</option>
              </select>
            </div>

            {/* Current Tools */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {fl.currentTools}
              </label>
              <Textarea
                data-testid="current-tools-input"
                value={businessInfo.currentTools}
                onChange={(e) => setBusinessInfo({...businessInfo, currentTools: e.target.value})}
                className="bg-[#141619] border-[#2a3441] text-white focus:border-[#D4AF37] min-h-[80px]"
                placeholder={fl.currentToolsPlaceholder}
              />
            </div>

            {/* Target Audience */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {fl.targetAudience}
              </label>
              <Textarea
                data-testid="target-audience-input"
                value={businessInfo.targetAudience}
                onChange={(e) => setBusinessInfo({...businessInfo, targetAudience: e.target.value})}
                className="bg-[#141619] border-[#2a3441] text-white focus:border-[#D4AF37] min-h-[80px]"
                placeholder={fl.targetAudiencePlaceholder}
              />
            </div>

            {/* Timeline & Budget Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {fl.timeline}
                </label>
                <select
                  data-testid="timeline-select"
                  value={businessInfo.timeline}
                  onChange={(e) => setBusinessInfo({...businessInfo, timeline: e.target.value})}
                  className="w-full px-4 py-2 bg-[#141619] border border-[#2a3441] text-white rounded-md focus:border-[#D4AF37] focus:outline-none"
                >
                  <option value="">{language === 'en' ? 'Select timeline...' : language === 'es' ? 'Seleccionar plazo...' : 'Zeitrahmen wählen...'}</option>
                  <option value="asap">{fl.timelineOptions.asap}</option>
                  <option value="1-2weeks">{fl.timelineOptions['1-2weeks']}</option>
                  <option value="1month">{fl.timelineOptions['1month']}</option>
                  <option value="2-3months">{fl.timelineOptions['2-3months']}</option>
                  <option value="flexible">{fl.timelineOptions.flexible}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {fl.monthlyBudget}
                </label>
                <select
                  data-testid="monthly-budget-select"
                  value={businessInfo.monthlyBudget}
                  onChange={(e) => setBusinessInfo({...businessInfo, monthlyBudget: e.target.value})}
                  className="w-full px-4 py-2 bg-[#141619] border border-[#2a3441] text-white rounded-md focus:border-[#D4AF37] focus:outline-none"
                >
                  <option value="">{language === 'en' ? 'Select budget...' : language === 'es' ? 'Seleccionar presupuesto...' : 'Budget wählen...'}</option>
                  <option value="under500">{fl.budgetOptions.under500}</option>
                  <option value="500-1000">{fl.budgetOptions['500-1000']}</option>
                  <option value="1000-2500">{fl.budgetOptions['1000-2500']}</option>
                  <option value="2500-5000">{fl.budgetOptions['2500-5000']}</option>
                  <option value="over5000">{fl.budgetOptions.over5000}</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                data-testid="cancel-business-form-btn"
                onClick={() => setShowBusinessForm(false)}
                className="flex-1 px-6 py-3 bg-[#2a3441] text-gray-300 rounded-full font-medium hover:bg-[#3a4451] transition-colors duration-300"
              >
                {fl.cancel}
              </button>
              <button
                type="submit"
                data-testid="submit-business-form-btn"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#F4C542] text-[#141619] rounded-full font-bold hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300"
              >
                {fl.addToCart}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Services;
